import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import { useEffect } from "react";
import { FaEdit, FaTrash, FaTrashRestore } from "react-icons/fa";
import ConfirmDialog from "../../components/ConfirmDialog";

const StockList = () => {
  const [items, setItems] = useState([]);
  const [trays, setTrays] = useState([]);
  const [open, setOpen] = useState(false);
  const [trayOpen, setTrayOpen] = useState(false);

  const [itemName, setItemName] = useState("");
  const [trayName, setTrayName] = useState("");
  const [trayPrice, setTrayPrice] = useState("");
  const [error, setError] = useState("");
  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedTrayId, setSelectedTrayId] = useState("");
  const [editItem, setEditItem] = useState(false);
  const [editTray, setEditTray] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDeleteTray, setOpenDeleteTray] = useState(false);
  const [openActivateTray, setOpenActivateTray] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getItemsList();
    getTraysList();
  }, []);

  const handleAddItem = async () => {
    try {
      setSaving(true);
      if (!itemName.trim()) {
        setError("Item name is required");
        setSaving(false)
        return;
      }

      const payload = {
        itemName: itemName
      };

      let path = "/stock/add-item";

      if (editTray) {
        path = "/stock/update/" + selectedItemId;
      }

      const response = await axiosInstance.post(path, payload);

      toast.success(response?.data?.message || "Item added succesfully");

      getItemsList();
      closeAddItemPopup();
      setSaving(false);
    } catch (error) {
      setSaving(false);
      toast.error(
        error.response?.data?.message ||
          "Something went wrong, try again after sometime"
      );
    }
  };

  const handleAddTray = async () => {
    try {
      setSaving(true);
      if (!trayName.trim() || !trayPrice) {
        setError("All fields are required");
        setSaving(false)
        return;
      }

      const payload = {
        name: trayName,
        price: trayPrice
      };

      let path = "/trays";

      if (editTray) {
        path = "/trays/" + selectedTrayId;
      }

      const response = await axiosInstance.post(path, payload);

      toast.success(response?.data?.message || "Tray added succesfully");

      getTraysList();
      closeAddTrayPopup();
      setSaving(false);
    } catch (error) {
      setSaving(false);
      console.log(error)
      toast.error(
        error.response?.data?.message ||
          "Something went wrong, try again after sometime"
      );
    }
  };

  const closeAddItemPopup = () => {
    setOpen(false);
    setError("");
    setItemName("");
    setEditItem(false);
  };

  const closeAddTrayPopup = () => {
    setTrayOpen(false);
    setError("");
    setTrayName("");
    setTrayPrice("");
    setEditTray(false);
  };

  const getItemsList = async () => {
    try {
      const response = await axiosInstance.get("/stock");

      setItems(response?.data?.list);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error retrieving list");
    }
  };

  const getTraysList = async () => {
    try {
      const response = await axiosInstance.get("/trays");

      const list = response?.data?.list

      setTrays(list);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error retrieving list");
    }
  };

  const openEditItemPopup = (item) => {
    setEditItem(true);
    setSelectedItemId(item?._id);
    setItemName(item?.itemName);
    setOpen(true);
  };

  const openEditTrayPopup = (item) => {
    setEditTray(true);
    setSelectedTrayId(item?._id);
    setTrayName(item?.name);
    setTrayPrice(item?.price);
    setTrayOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      await axiosInstance.delete("stock/" + selectedItemId);
      getItemsList();
      setOpenDelete(false);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  const handleDeleteTray = async () => {
    try {
      await axiosInstance.delete("trays/" + selectedTrayId);
      getTraysList();
      setOpenDeleteTray(false);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  const handleActivateTray = async () => {
    try {
      const response = await axiosInstance.post("trays/" + selectedTrayId, { status: true });
      toast.success(response?.data?.message || "Tray activated successfully");
      getTraysList();
      setOpenActivateTray(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Something went wrong, try again after sometime"
      );
    }
  };

  const getItemsRowStyle = () => {
    const leftBorderColor = '#6366f1'; // Premium Indigo
    const bgColor = 'rgba(99, 102, 241, 0.005)';
    const borderColor = 'rgba(99, 102, 241, 0.08)';
    const hoverBg = 'rgba(99, 102, 241, 0.02) !important';
    const hoverBorderColor = 'rgba(99, 102, 241, 0.18)';

    return {
      backgroundColor: bgColor,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.01)',
      '& td': {
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: borderColor,
        backgroundColor: 'transparent',
      },
      '& td:first-of-type': {
        borderLeft: '1px solid',
        borderTopLeftRadius: '12px',
        borderBottomLeftRadius: '12px',
        borderColor: borderColor,
        borderLeftColor: `${leftBorderColor} !important`,
        borderLeftWidth: '4px !important',
      },
      '& td:last-of-type': {
        borderRight: '1px solid',
        borderTopRightRadius: '12px',
        borderBottomRightRadius: '12px',
        borderColor: borderColor,
      },
      "&:hover": {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)',
        backgroundColor: hoverBg,
        '& td': {
          borderColor: hoverBorderColor,
        }
      }
    };
  };

  const getTraysRowStyle = (status) => {
    const isInactive = status === false;
    const leftBorderColor = isInactive ? '#ef4444' : '#0ea5e9'; // Red for Inactive, Sky Blue for Active
    const bgColor = isInactive ? 'rgba(239, 68, 68, 0.005)' : 'rgba(14, 165, 233, 0.005)';
    const borderColor = isInactive ? 'rgba(239, 68, 68, 0.08)' : 'rgba(14, 165, 233, 0.08)';
    const hoverBg = isInactive ? 'rgba(239, 68, 68, 0.02) !important' : 'rgba(14, 165, 233, 0.02) !important';
    const hoverBorderColor = isInactive ? 'rgba(239, 68, 68, 0.18)' : 'rgba(14, 165, 233, 0.18)';

    return {
      backgroundColor: bgColor,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.01)',
      '& td': {
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: borderColor,
        backgroundColor: 'transparent',
      },
      '& td:first-of-type': {
        borderLeft: '1px solid',
        borderTopLeftRadius: '12px',
        borderBottomLeftRadius: '12px',
        borderColor: borderColor,
        borderLeftColor: `${leftBorderColor} !important`,
        borderLeftWidth: '4px !important',
      },
      '& td:last-of-type': {
        borderRight: '1px solid',
        borderTopRightRadius: '12px',
        borderBottomRightRadius: '12px',
        borderColor: borderColor,
      },
      "&:hover": {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)',
        backgroundColor: hoverBg,
        '& td': {
          borderColor: hoverBorderColor,
        }
      }
    };
  };

  return (
    <Box p={3}>
      <Box display="flex" gap={4}>
        {/* ================= ITEMS TABLE ================= */}
        <Box flex={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight="bold">
              Items
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              sx={{
                textTransform: 'none',
                borderRadius: '20px',
                fontWeight: 600,
                px: 2.5,
                py: 0.75,
                background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 16px rgba(99, 102, 241, 0.3)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              }}
            >
              Add Item
            </Button>
          </Box>

          <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 'none', backgroundColor: 'transparent' }}>
            <TableContainer sx={{ maxHeight: 550, overflowY: "auto" }}>
              <Table stickyHeader sx={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                <TableHead>
                  <TableRow sx={{ '& th': { border: 'none', backgroundColor: '#f8fafc', color: 'text.secondary', py: 1.5 } }}>
                    <TableCell>
                      <b>Item Name</b>
                    </TableCell>
                    <TableCell>
                      <b>Actions</b>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No items found
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => {
                      const itemRowStyle = getItemsRowStyle();
                      return (
                        <TableRow key={item._id} sx={itemRowStyle}>
                          <TableCell sx={{ py: 1.5, fontWeight: 600, color: 'text.primary' }}>
                            {item.itemName}
                          </TableCell>

                          <TableCell sx={{ py: 1 }}>
                            <IconButton
                              size="small"
                              sx={{
                                color: '#4f46e5',
                                backgroundColor: 'rgba(99, 102, 241, 0.05)',
                                '&:hover': {
                                  backgroundColor: 'rgba(99, 102, 241, 0.15)',
                                  transform: 'scale(1.05)'
                                },
                                marginRight: 1,
                                transition: 'all 0.2s',
                              }}
                              onClick={() => openEditItemPopup(item)}
                            >
                              <FaEdit size={15} />
                            </IconButton>

                            <IconButton
                              size="small"
                              sx={{
                                color: '#ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.05)',
                                '&:hover': {
                                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                  transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s',
                              }}
                              onClick={() => { setSelectedItemId(item?._id); setOpenDelete(true); }}
                            >
                              <FaTrash size={13} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* ================= TRAYS TABLE ================= */}
        <Box flex={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight="bold">
              Trays
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setTrayOpen(true)}
              sx={{
                textTransform: 'none',
                borderRadius: '20px',
                fontWeight: 600,
                px: 2.5,
                py: 0.75,
                background: 'linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%)',
                boxShadow: '0 4px 12px rgba(14, 165, 233, 0.2)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0369a1 0%, #0284c7 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 16px rgba(14, 165, 233, 0.3)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              }}
            >
              Add Tray
            </Button>
          </Box>

          <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 'none', backgroundColor: 'transparent' }}>
            <TableContainer sx={{ maxHeight: 550, overflowY: "auto" }}>
              <Table stickyHeader sx={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                <TableHead>
                  <TableRow sx={{ '& th': { border: 'none', backgroundColor: '#f8fafc', color: 'text.secondary', py: 1.5 } }}>
                    <TableCell>
                      <b>Tray Name</b>
                    </TableCell>
                    <TableCell>
                      <b>Price</b>
                    </TableCell>
                    <TableCell>
                      <b>Actions</b>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {trays.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No trays found
                      </TableCell>
                    </TableRow>
                  ) : (
                    trays.map((item) => {
                      const trayRowStyle = getTraysRowStyle(item.status);
                      return (
                        <TableRow key={item._id} sx={trayRowStyle}>
                          <TableCell sx={{ py: 1.5, fontWeight: 600, color: 'text.primary' }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {item.name}
                              {item.status === false && (
                                <Chip
                                  label="Inactive"
                                  color="error"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: '20px',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    borderColor: 'rgba(239, 68, 68, 0.3)',
                                    color: '#ef4444',
                                    backgroundColor: 'rgba(239, 68, 68, 0.05)',
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1.5, fontWeight: 600 }}>
                            ₹{item.price}
                          </TableCell>

                          <TableCell sx={{ py: 1 }}>
                            {item.status !== false ? (
                              <Tooltip title="Delete Tray" arrow placement="top">
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: '#ef4444',
                                    backgroundColor: 'rgba(239, 68, 68, 0.05)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                      transform: 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s',
                                  }}
                                  onClick={() => { setSelectedTrayId(item?._id); setOpenDeleteTray(true); }}
                                >
                                  <FaTrash size={13} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Activate Tray" arrow placement="top">
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: '#10b981',
                                    backgroundColor: 'rgba(16, 185, 129, 0.05)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(16, 185, 129, 0.15)',
                                      transform: 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s',
                                  }}
                                  onClick={() => { setSelectedTrayId(item._id); setOpenActivateTray(true); }}
                                >
                                  <FaTrashRestore size={13} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>

      {/* ================= ADD ITEM DIALOG ================= */}
      <Dialog open={open} fullWidth maxWidth="sm">
        <DialogTitle>Add Item</DialogTitle>

        <DialogContent>
          <TextField
            label="Item Name"
            fullWidth
            margin="normal"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => closeAddItemPopup()}>Cancel</Button>

          <Button variant="contained" onClick={handleAddItem}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= ADD SERVICE DIALOG ================= */}
      <Dialog open={trayOpen} fullWidth maxWidth="sm">
        <DialogTitle>Add Item</DialogTitle>

        <DialogContent>
          <TextField
            label="Tray Name"
            fullWidth
            margin="normal"
            value={trayName}
            onChange={(e) => setTrayName(e.target.value)}
          />

          <TextField
            label="Price"
            type="number"
            onWheel={(e) => e.target.blur()}
            value={trayPrice}
            fullWidth
            onChange={(e) => setTrayPrice(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => closeAddTrayPopup()}>Cancel</Button>

          <Button variant="contained" onClick={handleAddTray}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={openDelete || openDeleteTray}
        onClose={()=> openDelete ? setOpenDelete(false) : setOpenDeleteTray(false)}
        onConfirm={() => openDelete ? handleDeleteItem() : handleDeleteTray()}
        message={`Are you sure you want to delete ${openDelete ? 'item' : 'tray'}?`}
      />
      <ConfirmDialog
        open={openActivateTray}
        onClose={() => setOpenActivateTray(false)}
        onConfirm={handleActivateTray}
        title="Activate Tray"
        confirmText="Activate"
        confirmColor="success"
        message="Are you sure you want to activate this tray?"
      />
    </Box>
  );
};

export default StockList;